/**
 * Authentication API service
 * Handles all PC authentication related API calls
 * Based on PC_AUTH_API.md documentation
 */

import client from './client'

/**
 * Send verification code to phone
 * @param {string} phone - Phone number (11 digits, starting with 1)
 * @param {number} purpose - Purpose: 1=register, 2=login, 3=reset password
 * @returns {Promise<object>} Response with expires_in (seconds)
 */
export async function sendVerificationCode(phone, purpose) {
  try {
    const response = await client.post('/pc-auth/verification-code', {
      phone,
      purpose
    })
    return response.data
  } catch (error) {
    return handleApiError(error)
  }
}

/**
 * Register new user
 * @param {string} phone - Phone number
 * @param {string} code - Verification code (6 digits)
 * @param {string} password - Password (must include uppercase, lowercase, number, min 8 chars)
 * @param {string} nickname - User nickname (optional)
 * @param {string} company - Company name (optional)
 * @returns {Promise<object>} Response with token, user_id, phone, nickname, company, user_type, user_type_name
 */
export async function register(phone, code, password, nickname = '', company = '', attribution = {}) {
  try {
    const response = await client.post('/pc-auth/register', {
      phone,
      code,
      password,
      nickname,
      company,
      invite_code: attribution.invite_code || undefined,
      attribution_source: attribution.attribution_source || 'pc_organic',
      utm_params: attribution.utm_params || {}
    })
    return response.data
  } catch (error) {
    return handleApiError(error)
  }
}

/**
 * Login with password
 * @param {string} phone - Phone number
 * @param {string} password - Password
 * @returns {Promise<object>} Response with token, user_id, phone, nickname, company, user_type, user_type_name
 */
export async function loginWithPassword(phone, password) {
  try {
    const response = await client.post('/pc-auth/login/password', {
      phone,
      password
    })
    return response.data
  } catch (error) {
    return handleApiError(error)
  }
}

/**
 * Login with verification code
 * @param {string} phone - Phone number
 * @param {string} code - Verification code (6 digits)
 * @returns {Promise<object>} Response with token, user_id, phone, nickname, company, user_type, user_type_name
 */
export async function loginWithCode(phone, code, attribution = {}) {
  try {
    const response = await client.post('/pc-auth/login/code', {
      phone,
      code,
      invite_code: attribution.invite_code || undefined,
      attribution_source: attribution.attribution_source || 'pc_organic',
      utm_params: attribution.utm_params || {}
    })
    return response.data
  } catch (error) {
    return handleApiError(error)
  }
}

/**
 * Reset password using verification code
 * @param {string} phone - Phone number
 * @param {string} code - Verification code
 * @param {string} newPassword - New password
 * @returns {Promise<object>} Response indicating success
 */
export async function resetPassword(phone, code, newPassword) {
  try {
    const response = await client.post('/pc-auth/reset-password', {
      phone,
      code,
      new_password: newPassword
    })
    return response.data
  } catch (error) {
    return handleApiError(error)
  }
}

/**
 * Set password for authenticated user who has no password yet (e.g. logged in via SMS code)
 * Requires valid JWT token in Authorization header
 * No old password or verification code needed
 * @param {string} phone - Phone number (must match current logged-in account)
 * @param {string} newPassword - New password to set
 * @param {string} confirmPassword - Confirm new password
 * @returns {Promise<object>} Response indicating success
 */
export async function setPassword(phone, newPassword, confirmPassword) {
  try {
    const response = await client.post('/pc-auth/set-password', {
      phone,
      new_password: newPassword,
      confirm_password: confirmPassword
    })
    return response.data
  } catch (error) {
    return handleApiError(error)
  }
}

/**
 * Change password for authenticated user
 * Requires valid JWT token in Authorization header
 * @param {string} oldPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<object>} Response indicating success
 */
export async function changePassword(oldPassword, newPassword) {
  try {
    const response = await client.put('/pc-auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword
    })
    return response.data
  } catch (error) {
    return handleApiError(error)
  }
}

/**
 * Handle API errors and return unified response
 * @param {Error} error - Axios error object
 * @returns {object} Error response with code and message
 */
function handleApiError(error) {
  if (error.response?.data) {
    // API returned error response
    return error.response.data
  }

  // Network or other error
  return {
    code: 0,
    message: error.message || '请求失败，请检查网络连接'
  }
}

export default {
  sendVerificationCode,
  register,
  loginWithPassword,
  loginWithCode,
  resetPassword,
  setPassword,
  changePassword
}
